import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * Page de connexion ou retour OIDC : pas de toolbox, pas de raccourcis (dont mode édition).
 */
export function isConnectionFlowRoutePath(path: string): boolean {
  if (path === '/login' || path.startsWith('/login/')) {
    return true
  }
  if (path.startsWith('/auth/oidc')) {
    return true
  }
  return false
}

/**
 * Routes où la boîte à outils (FAB + raccourcis) ne doit pas être proposée.
 */
function isBlockingRoutePath(path: string): boolean {
  if (isConnectionFlowRoutePath(path)) {
    return true
  }
  if (path === '/parametres' || path.startsWith('/parametres/')) {
    return true
  }
  return path === '/admin' || path.startsWith('/admin/');

}

/**
 * Modales plein écran (custom) ou couches Reka (Dialog / AlertDialog) ouvertes.
 */
function isBlockingModalPresentInDocument(): boolean {
  if (!import.meta.client) {
    return false
  }
  return (
    document.querySelector('.notice-acceptance-wall') !== null ||
    document.querySelector('.notice-modal-overlay') !== null ||
    document.querySelector('.admin-tags-delete-modal') !== null ||
    document.querySelector('[role="dialog"][data-state="open"]') !== null ||
    document.querySelector('[role="alertdialog"][data-state="open"]') !== null
  )
}

/**
 * Indique si la toolbox peut être affichée et si les raccourcis associés sont actifs.
 * (Connexion, paramètres, admin, ou une modale bloquante → pas d’accès.)
 */
export function useAppToolboxAvailability() {
  const route = useRoute()
  const modalLayerBlocks = ref(false)

  function refreshModalBlocking(): void {
    modalLayerBlocks.value = isBlockingModalPresentInDocument()
  }

  let observer: MutationObserver | null = null
  let scheduledFrameId = 0

  function scheduleRefreshModalBlocking(): void {
    if (!import.meta.client) {
      return
    }
    cancelAnimationFrame(scheduledFrameId)
    scheduledFrameId = requestAnimationFrame(() => {
      refreshModalBlocking()
    })
  }

  onMounted(() => {
    refreshModalBlocking()
    observer = new MutationObserver(() => {
      scheduleRefreshModalBlocking()
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'class'],
    })
  })

  onBeforeUnmount(() => {
    cancelAnimationFrame(scheduledFrameId)
    observer?.disconnect()
    observer = null
  })

  const isToolboxRouteBlocked = computed(() => isBlockingRoutePath(route.path))

  const isToolboxUiBlocked = computed(
    () => isToolboxRouteBlocked.value || modalLayerBlocks.value,
  )

  const isConnectionRoute = computed(() => isConnectionFlowRoutePath(route.path))

  return {
    isToolboxUiBlocked,
    isToolboxRouteBlocked,
    isConnectionRoute,
    modalLayerBlocks,
  }
}
